import React, { useState } from 'react';
import { createProduct, startKeepaPull, runUnmatched } from '../api';

const MARKETPLACES = [
  { code: 'GB', label: 'GB — amazon.co.uk' },
  { code: 'US', label: 'US — amazon.com' },
  { code: 'DE', label: 'DE — amazon.de' },
  { code: 'FR', label: 'FR — amazon.fr' },
  { code: 'IT', label: 'IT — amazon.it' },
  { code: 'ES', label: 'ES — amazon.es' },
];

function getSavedMarketplace(): string {
  try {
    const saved = JSON.parse(localStorage.getItem('asin-tracker-settings') || '{}');
    return saved.defaultMarketplace || 'GB';
  } catch {
    return 'GB';
  }
}

interface AddProductProps {
  onCreated: (id: string, jobId?: string) => void;
}

const AddProduct: React.FC<AddProductProps> = ({ onCreated }) => {
  const [form, setForm] = useState({
    internal_sku: '',
    title: '',
    brand: '',
    ean: '',
    upc: '',
    mpn: '',
    category: '',
  });
  const [marketplace, setMarketplace] = useState(getSavedMarketplace);
  const [step, setStep] = useState<'idle' | 'creating' | 'searching'>('idle');
  const [error, setError] = useState('');

  const loading = step !== 'idle';

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.internal_sku || !form.title) {
      setError('SKU and Title are required');
      return;
    }
    setError('');

    // ── Step 1: Create the product ────────────────────────────────────────
    setStep('creating');
    let productId: string;
    try {
      const data: any = { internal_sku: form.internal_sku, title: form.title };
      if (form.brand)    data.brand    = form.brand;
      if (form.ean)      data.ean      = form.ean;
      if (form.upc)      data.upc      = form.upc;
      if (form.mpn)      data.mpn      = form.mpn;
      if (form.category) data.category = form.category;
      const res = await createProduct(data);
      productId = res.data.id;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create product');
      setStep('idle');
      return;
    }

    // ── Step 2: Auto-trigger Keepa search ────────────────────────────────
    // Priority: EAN/UPC barcode search first, title search as fallback.
    setStep('searching');
    let jobId: string | undefined;

    const hasBarcode = !!(form.ean || form.upc);

    if (hasBarcode) {
      try {
        const res = await startKeepaPull(marketplace, undefined, [productId]);
        jobId = res.data.job_id;
      } catch {
        // barcode pull failed — fall through to title search
      }
    }

    if (!jobId) {
      try {
        const res = await runUnmatched(marketplace, [productId]);
        jobId = res.data.job_id;
      } catch {
        // title search also failed — navigate without a job; user can pull manually
      }
    }

    onCreated(productId, jobId);
  };

  const stepLabel = step === 'creating'
    ? 'Creating product…'
    : step === 'searching'
      ? (form.ean || form.upc ? 'Searching Keepa by barcode…' : 'Searching Keepa by title…')
      : 'Create Product & Start Matching';

  return (
    <div>
      <div className="page-header">
        <h2>Add New Product</h2>
        <p>Product will be created and a Keepa search started automatically.</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card" style={{ maxWidth: 620 }}>
        <form onSubmit={handleSubmit}>
          {/* SKU + Category */}
          <div className="form-row">
            <div className="form-group">
              <label>Internal SKU *</label>
              <input
                value={form.internal_sku}
                onChange={(e) => handleChange('internal_sku', e.target.value)}
                placeholder="e.g. PROD-001"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <input
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder="e.g. Electronics"
                disabled={loading}
              />
            </div>
          </div>

          {/* Title */}
          <div className="form-group">
            <label>Product Title *</label>
            <input
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g. Nike Air Max 90 Men's Running Shoes"
              disabled={loading}
            />
          </div>

          {/* Brand + MPN */}
          <div className="form-row">
            <div className="form-group">
              <label>Brand</label>
              <input
                value={form.brand}
                onChange={(e) => handleChange('brand', e.target.value)}
                placeholder="e.g. Nike"
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>MPN</label>
              <input
                value={form.mpn}
                onChange={(e) => handleChange('mpn', e.target.value)}
                placeholder="Manufacturer Part Number"
                disabled={loading}
              />
            </div>
          </div>

          {/* EAN + UPC */}
          <div className="form-row">
            <div className="form-group">
              <label>EAN (13 digits)</label>
              <input
                value={form.ean}
                onChange={(e) => handleChange('ean', e.target.value)}
                placeholder="e.g. 1234567890123"
                maxLength={13}
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label>UPC (12 digits)</label>
              <input
                value={form.upc}
                onChange={(e) => handleChange('upc', e.target.value)}
                placeholder="e.g. 123456789012"
                maxLength={12}
                disabled={loading}
              />
            </div>
          </div>

          {/* Marketplace selector */}
          <div className="form-group">
            <label>Keepa Search Marketplace</label>
            <select
              value={marketplace}
              onChange={(e) => setMarketplace(e.target.value)}
              disabled={loading}
            >
              {MARKETPLACES.map(mp => (
                <option key={mp.code} value={mp.code}>{mp.label}</option>
              ))}
            </select>
            <p className="settings-field-hint">
              {form.ean || form.upc
                ? 'Will search by barcode (EAN/UPC) first, then fall back to title.'
                : 'No barcode provided — will search by product title.'}
            </p>
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
            style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}
          >
            {loading && (
              <svg className="column-toggle-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            )}
            {stepLabel}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddProduct;
